import { Mark, mergeAttributes } from '@tiptap/core';

import {
  RICH_TEXT_COLOR_CLOSE_TAG,
  RICH_TEXT_COLOR_OPEN_TAG_RE,
  normalizeRichTextColor,
  richTextColorOpenTag,
} from './text-color';

/**
 * 글자 색상 마크 (`AxRichTextEditor` 전용). 설계: `docs/designs/rich-text-color.md`
 *
 * 마크다운에는 색 문법이 없어 인라인 HTML `<span style="color: #hex">` 로 저장한다.
 * 다만 `tiptap-markdown` 의 `html` 옵션은 **켜지 않는다** — 켜면 본문에 글자로 적힌
 * 모든 태그·HTML 주석(HR 문서의 `<!-- PARTY_TABLE -->` 마커 등)까지 해석되어
 * 저장 시 사라지기 때문이다. 대신 이 확장이 직접
 *
 * - 직렬화: 자체 markdown spec(`serialize.open/close`)으로 태그를 쓰고,
 * - 파싱: markdown-it 인라인 규칙을 하나 얹어 **프리셋 색 span 만** 태그로 통과시킨다.
 *
 * 그 결과 색 이외의 HTML 은 지금까지와 똑같이 글자 그대로 남는다.
 */

interface MarkdownItInlineState {
  src: string;
  pos: number;
  env: Record<string, unknown>;
  push: (type: string, tag: string, nesting: number) => { content: string };
}

interface MarkdownItLike {
  inline: {
    ruler: {
      before: (
        beforeName: string,
        ruleName: string,
        rule: (state: MarkdownItInlineState, silent: boolean) => boolean,
      ) => void;
    };
  };
}

const OPEN_DEPTH_ENV_KEY = 'axTextColorOpenDepth';

/**
 * `<span style="color: #프리셋">` / `</span>` 만 인라인 HTML 토큰으로 통과시키는 규칙.
 * 닫는 태그는 열린 span 이 있을 때만 소비해, 사용자가 글자로 적은 `</span>` 이
 * 조용히 사라지지 않게 한다.
 */
function axTextColorInlineRule(state: MarkdownItInlineState, silent: boolean): boolean {
  if (state.src.charCodeAt(state.pos) !== 0x3c /* < */) return false;
  const rest = state.src.slice(state.pos);

  const open = RICH_TEXT_COLOR_OPEN_TAG_RE.exec(rest);
  if (open) {
    const color = normalizeRichTextColor(open[1]);
    if (!color) return false;
    if (!silent) {
      state.push('html_inline', '', 0).content = richTextColorOpenTag(color);
      state.env[OPEN_DEPTH_ENV_KEY] = ((state.env[OPEN_DEPTH_ENV_KEY] as number) ?? 0) + 1;
    }
    state.pos += open[0].length;
    return true;
  }

  const depth = (state.env[OPEN_DEPTH_ENV_KEY] as number) ?? 0;
  if (depth > 0 && rest.startsWith(RICH_TEXT_COLOR_CLOSE_TAG)) {
    if (!silent) {
      state.push('html_inline', '', 0).content = RICH_TEXT_COLOR_CLOSE_TAG;
      state.env[OPEN_DEPTH_ENV_KEY] = depth - 1;
    }
    state.pos += RICH_TEXT_COLOR_CLOSE_TAG.length;
    return true;
  }

  return false;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    axTextColor: {
      /** 선택 영역 글자 색을 프리셋 색으로 지정한다. 프리셋 밖 값은 무시된다. */
      setTextColor: (color: string) => ReturnType;
      /** 선택 영역 글자 색을 해제한다(기본색). */
      unsetTextColor: () => ReturnType;
    };
  }
}

export const AxTextColor = Mark.create({
  name: 'axTextColor',
  // StarterKit 마크(굵게 등)보다 먼저 스키마에 올려 색이 **바깥 마크**가 되게 한다.
  // 안쪽이면 `<span>파랑 </span>**<span>굵게</span>**` 처럼 서식마다 span 이 쪼개진다.
  priority: 1000,

  addAttributes() {
    return {
      color: {
        default: null as string | null,
        parseHTML: (element) => normalizeRichTextColor(element.getAttribute('style')),
        renderHTML: (attributes) => {
          const color = normalizeRichTextColor(attributes.color as string | null);
          return color ? { style: `color: ${color}` } : {};
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[style]',
        getAttrs: (element: HTMLElement) => {
          const color = normalizeRichTextColor(element.getAttribute('style'));
          return color ? { color } : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setTextColor:
        (color: string) =>
        ({ chain }) => {
          const normalized = normalizeRichTextColor(color);
          if (!normalized) return false;
          return chain().setMark(this.name, { color: normalized }).run();
        },
      unsetTextColor:
        () =>
        ({ chain }) =>
          chain().unsetMark(this.name).run(),
    };
  },

  addStorage() {
    return {
      markdown: {
        serialize: {
          open: (_state: unknown, mark: { attrs: { color?: string | null } }) => {
            const color = normalizeRichTextColor(mark.attrs.color);
            return color ? richTextColorOpenTag(color) : '';
          },
          close: (_state: unknown, mark: { attrs: { color?: string | null } }) =>
            normalizeRichTextColor(mark.attrs.color) ? RICH_TEXT_COLOR_CLOSE_TAG : '',
        },
        parse: {
          setup: (markdownit: MarkdownItLike) => {
            markdownit.inline.ruler.before('text', 'axTextColor', axTextColorInlineRule);
          },
        },
      },
    };
  },
});
